// ClientVolnoSection — zadávání a přehled budoucího VOLNA klienta (dovolená, nemoc).
// Volno se promítne do rozvrh vzoru: booking klienta v období jeho volna se nezapočítá
// do detekce pravidelného termínu (backend trener-rozvrh-pattern + klient_rozvrh_absence).
//
// Použití: <ClientVolnoSection klientName={client.klient} klientId={client.notion_id} />

import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Plane, Plus, Trash2, RefreshCw, CalendarX, Umbrella, Thermometer, CalendarDays, Check, X, User } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import DatePicker from "./DatePicker.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";

const REASONS = [
  { value: "dovolena", label: "Dovolená", icon: Umbrella },
  { value: "nemoc", label: "Nemoc", icon: Thermometer },
  { value: "jine", label: "Jiné", icon: CalendarDays },
];
const REASON_LABEL = { dovolena: "Dovolená", nemoc: "Nemoc", jine: "Jiné volno" };
const REASON_TONE = {
  dovolena: "bg-sky-50 border-sky-200 text-sky-700",
  nemoc: "bg-rose-50 border-rose-200 text-rose-700",
  jine: "bg-slate-50 border-slate-200 text-slate-600",
};

function fmtCz(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}. ${m[1]}` : iso;
}
function daysBetween(from, to) {
  try {
    const a = new Date(from), b = new Date(to || from);
    if (isNaN(a) || isNaN(b)) return 0;
    return Math.round((b - a) / 86400000) + 1;
  } catch { return 0; }
}
function dayWord(n) {
  return n === 1 ? "den" : n >= 2 && n <= 4 ? "dny" : "dní";
}
function prettyUser(v) {
  const s = String(v || "").trim();
  if (!s || s === "admin") return s || "—";
  return s.includes("@") ? s.split("@")[0] : s;
}
const todayIso = () => new Date().toISOString().substring(0, 10);

// Nejdřívější povolené datum volna klienta. Pravidlo (shodné s backendem klient-rozvrh-absence):
// volno se plánuje na následující měsíc (do 20. dne aktuálního měsíce) a dál; po 20. až od přespříštího
// měsíce; aktuální měsíc nelze. Owner a CSM mají výjimku (od dneška).
function minVolnoDate(isOwner) {
  if (isOwner) return todayIso();
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth() + 1 + (now.getDate() > 20 ? 1 : 0), 1);
  return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function ClientVolnoSection({ klientName, klientId, openTrigger = 0, readOnly = false, bezRamce = false, onCount }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ dateFrom: "", dateTo: "", duvod: "dovolena", poznamka: "" });
  const rootRef = useRef(null);
  // 9.9.2026 — na telefonu je "Přidat volno" tlačítko přes celou šířku nad seznamem
  // a každé volno je karta, ať se datum, poznámka a koš netlačí do jednoho řádku.
  const uzka = useUzkaObrazovka();

  // 3.8.2026: CSM smi zadat volno klienta kdykoli, stejne jako owner (sprava klientu).
  // Flag "bez omezeni data" tedy plati pro owner i csm.
  const isOwnerRole = ["owner", "csm"].includes(String(getCurrentUser()?.role || "").toLowerCase());

  // Otevre formular a u role s omezenim predvyplni datum na prvni povoleny den (kalendar se otevre tam).
  const openForm = useCallback(() => {
    setAdding(true);
    setError(null);
    setForm((f) => (f.dateFrom ? f : { ...f, dateFrom: isOwnerRole ? "" : minVolnoDate(false) }));
  }, [isOwnerRole]);

  // Otevreni formulare (modal popup) z tlacitka v hlavicce detailu (openTrigger se inkrementuje).
  useEffect(() => {
    if (openTrigger > 0) openForm();
  }, [openTrigger, openForm]);

  const load = useCallback(async () => {
    if (!klientName) return;
    setLoading(true); setError(null);
    try {
      const r = await apiFetch(`/api/klient-rozvrh-absence?klient=${encodeURIComponent(klientName)}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setItems(Array.isArray(j.items) ? j.items : []);
    } catch (e) {
      setError(e.message || "Chyba");
      setItems([]);
    } finally { setLoading(false); }
  }, [klientName]);

  useEffect(() => { load(); }, [load]);

  // Počet naplánovaného volna do odznaku v hlavičce sekce (9.9.2026).
  useEffect(() => {
    if (typeof onCount !== "function") return;
    const dnes = todayIso();
    onCount((items || []).filter((x) => (x.date_to || x.date_from) >= dnes).length);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  async function add() {
    if (!form.dateFrom) { setError("Vyplň datum od"); return; }
    if (!isOwner && form.dateFrom < minVolnoDate(false)) {
      setError("Volno klienta se zadává na následující měsíc a dál (do 20. dne aktuálního měsíce). Aktuální měsíc řeš přes propadnutí tréninku.");
      return;
    }
    setBusy(true); setError(null);
    try {
      const r = await apiFetch("/api/klient-rozvrh-absence", {
        method: "POST",
        body: JSON.stringify({
          klient: klientName, klientId,
          dateFrom: form.dateFrom, dateTo: form.dateTo || form.dateFrom,
          duvod: form.duvod, poznamka: form.poznamka,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setForm({ dateFrom: "", dateTo: "", duvod: "dovolena", poznamka: "" });
      setAdding(false);
      await load();
    } catch (e) { setError(e.message || "Chyba"); }
    finally { setBusy(false); }
  }

  async function remove(id) {
    setBusy(true); setError(null);
    try {
      const r = await apiFetch("/api/klient-rozvrh-absence", { method: "DELETE", body: JSON.stringify({ id }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      await load();
    } catch (e) { setError(e.message || "Chyba"); }
    finally { setBusy(false); }
  }

  const today = todayIso();
  const isOwner = isOwnerRole;
  const minDate = minVolnoDate(isOwner);
  const list = items || [];
  const upcoming = list.filter((x) => (x.date_to || x.date_from) >= today);
  const formDays = daysBetween(form.dateFrom, form.dateTo || form.dateFrom);

  return (
    <div ref={rootRef} className={bezRamce ? "scroll-mt-24" : "bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden scroll-mt-24"}>
      {/* Hlavička. 9.9.2026: v detailu klienta sekci obaluje ExpandableSection, takže
          vlastní hlavičku nekreslíme (bezRamce) a vypadá stejně jako ostatní sekce. */}
      {!bezRamce && (
        <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-sky-50 to-white border-b border-slate-200 flex-wrap">
          <div className="p-2 rounded-xl bg-sky-100 text-sky-600">
            <Plane className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-base font-semibold text-slate-900 leading-tight">Volno klienta</div>
            <div className="text-xs text-slate-500">
              {upcoming.length > 0 ? `${upcoming.length} naplánováno` : "žádné naplánované volno"}
            </div>
          </div>
        </div>
      )}

      <div className={bezRamce ? "" : "p-4"}>
        {!readOnly && (uzka || bezRamce) && (
          <button
            type="button"
            onClick={openForm}
            className="mb-3 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-brand-deep px-4 text-sm font-bold text-white hover:bg-brand sm:w-auto"
          >
            <Plus className="h-4 w-4" /> Přidat volno
          </button>
        )}
        <p className="text-xs text-slate-500 mb-3 flex items-start gap-1.5">
          <CalendarX className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
          Naplánované volno se nezapočítá do detekce pravidelného termínu v rozvrh vzoru.
        </p>

        {/* Formulář jako modal popup — přes portál na <body>, aby byl VŽDY vystředěný
            v okně a nad vším (ClientDetail je sám fixed overlay, jinak se modal jevil
            "nahoře" mimo místo kliknutí). Vždy items-center = jasný popup. */}
        {adding && createPortal((
          <div className="fixed inset-0 z-[60] bg-brand-dark/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={() => { setAdding(false); setError(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-200">
              <div className="p-2 rounded-xl bg-sky-100 text-sky-600"><Plane className="w-4 h-4" /></div>
              <div className="min-w-0">
                <div className="text-base font-semibold text-slate-900 leading-tight">Volno klienta</div>
                <div className="text-xs text-slate-500">{upcoming.length > 0 ? `${upcoming.length} naplánováno` : "žádné naplánované volno"}</div>
              </div>
              <button type="button" onClick={() => { setAdding(false); setError(null); }} className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200"><X className="w-4 h-4" /> Zavřít</button>
            </div>
            <div className="p-5 space-y-3 bg-sky-50/30">
              <p className="text-xs text-slate-500 flex items-start gap-1.5">
                <CalendarX className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
                Naplánované volno se nezapočítá do detekce pravidelného termínu v rozvrh vzoru.
              </p>
              {!isOwner && (
                <p className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2 flex items-start gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Volno se plánuje na následující měsíc (zadej do 20. dne aktuálního měsíce) a dál. Aktuální měsíc řeš přes propadnutí tréninku.
                </p>
              )}
            {/* Důvod jako segmentované přepínače */}
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1.5">Důvod</div>
              <div className="grid grid-cols-3 gap-2">
                {REASONS.map((r) => {
                  const Icon = r.icon;
                  const active = form.duvod === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, duvod: r.value }))}
                      className={`flex items-center justify-center gap-1.5 text-sm font-medium rounded-xl border px-2 py-2 transition-all ${
                        active
                          ? "bg-white border-sky-400 text-sky-700 ring-2 ring-sky-200 shadow-sm"
                          : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                      }`}
                    >
                      <Icon className="w-4 h-4" /> {r.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Datumy */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="text-xs font-medium text-slate-600">
                Od
                <div className="mt-1">
                  <DatePicker
                    value={form.dateFrom}
                    min={minDate}
                    onChange={(v) => setForm((f) => ({ ...f, dateFrom: v, dateTo: f.dateTo && f.dateTo < v ? v : f.dateTo }))}
                  />
                </div>
              </div>
              <div className="text-xs font-medium text-slate-600">
                Do
                <div className="mt-1">
                  <DatePicker
                    value={form.dateTo}
                    min={form.dateFrom || minDate}
                    onChange={(v) => setForm((f) => ({ ...f, dateTo: v }))}
                  />
                </div>
              </div>
            </div>

            {/* Poznámka */}
            <label className="block text-xs font-medium text-slate-600">
              Poznámka <span className="text-slate-400 font-normal">(volitelné)</span>
              <input
                type="text" value={form.poznamka} placeholder="Např. dovolená u moře"
                onChange={(e) => setForm((f) => ({ ...f, poznamka: e.target.value }))}
                className="field mt-1 w-full"
              />
            </label>

            {error && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{error}</div>
            )}

            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <button
                type="button" disabled={busy || !form.dateFrom} onClick={add}
                className="btn btn-primary"
              >
                {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Uložit volno
              </button>
              {form.dateFrom && (
                <span className="text-xs text-slate-500">
                  {fmtCz(form.dateFrom)}{form.dateTo && form.dateTo !== form.dateFrom ? ` – ${fmtCz(form.dateTo)}` : ""}, {formDays} {dayWord(formDays)}
                </span>
              )}
            </div>
            </div>
          </div>
          </div>
        ), document.body)}

        {/* Chyba mimo formulář */}
        {!adding && error && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 mb-3">{error}</div>
        )}

        {loading && (
          <div className="text-sm text-slate-500 flex items-center gap-2 py-2">
            <RefreshCw className="w-4 h-4 animate-spin" /> Načítám…
          </div>
        )}

        {!loading && list.length === 0 && (
          <div className="text-sm text-slate-400 italic py-3 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
            Žádné zadané volno.
          </div>
        )}

        {!loading && list.length > 0 && (
          <div className="space-y-2">
            {list.map((x) => {
              const past = (x.date_to || x.date_from) < today;
              const dd = daysBetween(x.date_from, x.date_to);
              // Telefon: důvod a délka jako odznaky nahoře, datum na vlastním řádku,
              // poznámka přes celou šířku a koš v patičce karty (9.9.2026).
              if (uzka) {
                return (
                  <div key={x.id} className={`rounded-xl border border-slate-200 ${past ? "bg-slate-50" : "bg-white"}`}>
                    <div className="flex flex-wrap items-center gap-1.5 px-3 pt-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-[11.5px] font-bold ${REASON_TONE[x.duvod] || REASON_TONE.jine}`}>
                        {REASON_LABEL[x.duvod] || x.duvod || "volno"}
                      </span>
                      {past && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11.5px] font-bold text-slate-400">už proběhlo</span>}
                      <span className="ml-auto text-[12px] font-semibold text-slate-400">{dd} {dayWord(dd)}</span>
                    </div>
                    <div className={`px-3 pt-1.5 text-[15px] font-bold leading-tight tabular-nums ${past ? "text-slate-500" : "text-slate-800"}`}>
                      {fmtCz(x.date_from)}{x.date_to && x.date_to !== x.date_from ? ` – ${fmtCz(x.date_to)}` : ""}
                    </div>
                    {x.poznamka ? <div className="px-3 pt-1 text-[13px] leading-snug text-slate-600">{x.poznamka}</div> : null}
                    <div className="mt-2 flex items-center gap-2 border-t border-slate-100 px-3 py-1.5">
                      <span className="text-[11.5px] text-slate-500">
                        zadal {prettyUser(x.zadavatel)}{x.created_at ? `, ${fmtCz(x.created_at)}` : ""}
                      </span>
                      {!readOnly && (
                        <button
                          type="button" disabled={busy} onClick={() => remove(x.id)}
                          aria-label="Smazat volno" title="Smazat volno"
                          className="ml-auto flex h-9 w-9 items-center justify-center rounded-xl text-rose-500 disabled:opacity-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={x.id}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-colors ${
                    past ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200 hover:border-sky-200"
                  }`}
                >
                  <div className={`p-1.5 rounded-xl shrink-0 ${past ? "bg-slate-100 text-slate-400" : "bg-sky-50 text-sky-500"}`}>
                    <CalendarX className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800 tabular-nums">
                      {fmtCz(x.date_from)}{x.date_to && x.date_to !== x.date_from ? ` – ${fmtCz(x.date_to)}` : ""}
                      <span className="ml-2 text-xs font-normal text-slate-400">{dd} {dayWord(dd)}</span>
                    </div>
                    {x.poznamka && <div className="text-xs text-slate-500 truncate">{x.poznamka}</div>}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5" title="Kdo volno zadal">
                        <User className="w-3 h-3 text-slate-400" />zadal {prettyUser(x.zadavatel)}
                      </span>
                      {x.created_at && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 tabular-nums" title="Datum zadání">
                          <CalendarDays className="w-3 h-3 text-slate-400" />{fmtCz(x.created_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-lg border shrink-0 ${REASON_TONE[x.duvod] || REASON_TONE.jine}`}>
                    {REASON_LABEL[x.duvod] || x.duvod || "volno"}
                  </span>
                  {!readOnly && (
                    <button
                      type="button" disabled={busy} onClick={() => remove(x.id)}
                      title="Smazat volno"
                      className="text-slate-400 hover:text-rose-600 disabled:opacity-50 shrink-0 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
